import { useEffect, useMemo, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';
import { Watermark } from './Watermark';

/**
 * Latest Events — v2 port of v1's latest-events.ejs.
 * Stores the most recent event of each enabled type and shows them either
 * side-by-side (bar) or rotating one at a time (rotate).
 *
 * Config (all optional):
 *   displayMode: 'bar' | 'rotate'                     (default 'bar')
 *   rotationSpeed: seconds between rotations          (default 4)
 *   accentColor: string                               (default '#8b5cf6')
 *   textColor: string                                 (default '#ffffff')
 *   bgStyle: 'semi' | 'solid' | 'transparent'         (default 'semi')
 *   fontSize: number                                  (default 14)
 *   showPlatformIcon: boolean                         (default true)
 *   enabledEvents: string[]                           (default common five)
 */

type Slot =
  | 'follow'
  | 'sub'
  | 'sub.gift'
  | 'raid'
  | 'tip'
  | 'cardRedemption'
  | 'channelPointRedemption';

interface SlotState {
  slot: Slot;
  label: string;
  name: string;
  amount: string;
  platform: string;
}

// "Latest Tip" is the unified monetization slot — HypeVault coin tips,
// Twitch bits, Kick kicks, YouTube SuperChats + Super Stickers all route
// here. The platform dot on the right of each row tells viewers which
// source it came from.
const SLOT_LABEL: Record<Slot, string> = {
  follow: 'Latest Follower',
  sub: 'Latest Sub',
  'sub.gift': 'Latest Gift Sub',
  raid: 'Latest Raid',
  tip: 'Latest Tip',
  cardRedemption: 'Latest Redemption',
  channelPointRedemption: 'Latest Channel Pts',
};

const DEFAULT_ENABLED: Slot[] = ['follow', 'sub', 'sub.gift', 'tip', 'raid'];

function eventToSlot(e: StreamEvent): SlotState | null {
  const platform = e.platform;
  switch (e.type) {
    case 'follow':
      return { slot: 'follow', label: SLOT_LABEL.follow, name: e.viewerName, amount: '', platform };
    case 'sub':
      if (e.isGift) {
        return {
          slot: 'sub.gift',
          label: SLOT_LABEL['sub.gift'],
          name: e.gifterName ?? 'Anonymous',
          amount: '',
          platform,
        };
      }
      return { slot: 'sub', label: SLOT_LABEL.sub, name: e.viewerName, amount: '', platform };
    case 'cheer':
      // Twitch bits + Kick kicks fold into the unified Tip slot.
      return {
        slot: 'tip',
        label: SLOT_LABEL.tip,
        name: e.viewerName,
        amount: `${e.bits.toLocaleString()} ${platform === 'kick' ? 'kicks' : 'bits'}`,
        platform,
      };
    case 'raid':
      return {
        slot: 'raid',
        label: SLOT_LABEL.raid,
        name: e.raiderName,
        amount: `${e.viewers.toLocaleString()} viewers`,
        platform,
      };
    case 'tip':
      return {
        slot: 'tip',
        label: SLOT_LABEL.tip,
        name: e.viewerName,
        amount: `${e.coins.toLocaleString()} coins`,
        platform,
      };
    case 'tip_received':
      return {
        slot: 'tip',
        label: SLOT_LABEL.tip,
        name: e.viewerName,
        amount: `$${(e.amountCents / 100).toFixed(2)}`,
        platform,
      };
    case 'superChat':
      return {
        slot: 'tip',
        label: SLOT_LABEL.tip,
        name: e.viewerName,
        amount: `$${(e.amountCents / 100).toFixed(2)} superchat`,
        platform,
      };
    case 'superSticker':
      return {
        slot: 'tip',
        label: SLOT_LABEL.tip,
        name: e.viewerName,
        amount: `$${(e.amountCents / 100).toFixed(2)} sticker`,
        platform,
      };
    case 'cardRedemption':
      return {
        slot: 'cardRedemption',
        label: SLOT_LABEL.cardRedemption,
        name: e.viewerName,
        amount: e.cardName,
        platform,
      };
    case 'channelPointRedemption':
      return {
        slot: 'channelPointRedemption',
        label: SLOT_LABEL.channelPointRedemption,
        name: e.viewerName,
        amount: `${e.rewardTitle} · ${e.rewardCost.toLocaleString()} pts`,
        platform,
      };
    default:
      return null;
  }
}

const SLOT_ICON: Record<Slot, string> = {
  follow:
    'M4.3 6.3a4.5 4.5 0 000 6.4L12 20.4l7.7-7.7a4.5 4.5 0 00-6.4-6.4L12 7.6l-1.3-1.3a4.5 4.5 0 00-6.4 0z',
  sub: 'M11 2.9a1 1 0 011.9 0l1.5 4.7a1 1 0 00.95.7H20c1 0 1.4 1.2.6 1.8l-4 2.9a1 1 0 00-.4 1.1l1.5 4.7c.3.9-.75 1.7-1.5 1.1l-4-2.9a1 1 0 00-1.2 0l-4 2.9c-.78.6-1.84-.2-1.54-1.1L7 14a1 1 0 00-.36-1.1l-4-2.9c-.78-.6-.38-1.8.59-1.8h4.91a1 1 0 00.95-.7L11 2.9z',
  'sub.gift':
    'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7',
  raid: 'M13 10V3L4 14h7v7l9-11h-7z',
  tip: 'M12 8c-1.66 0-3 .9-3 2s1.34 2 3 2 3 .9 3 2-1.34 2-3 2m0-8c1.1 0 2.08.4 2.6 1M12 8V7m0 1v8m0 0v1m0-1c-1.1 0-2.08-.4-2.6-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  cardRedemption:
    'M7 21h10a2 2 0 002-2V9.4a1 1 0 00-.3-.7l-5.4-5.4A1 1 0 0012.6 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  channelPointRedemption:
    'M11 2.9a1 1 0 011.9 0l1.5 4.7a1 1 0 00.95.7H20c1 0 1.4 1.2.6 1.8l-4 2.9a1 1 0 00-.4 1.1l1.5 4.7c.3.9-.75 1.7-1.5 1.1l-4-2.9a1 1 0 00-1.2 0l-4 2.9c-.78.6-1.84-.2-1.54-1.1L7 14a1 1 0 00-.36-1.1l-4-2.9c-.78-.6-.38-1.8.59-1.8h4.91a1 1 0 00.95-.7L11 2.9z',
};

const PLATFORM_BADGE: Record<string, string> = {
  twitch: '#9146ff',
  youtube: '#ff0000',
  kick: '#53fc18',
  hypevault: '#f655a6',
};

function backgroundFor(bgStyle: string) {
  if (bgStyle === 'solid') {
    return {
      background: 'linear-gradient(135deg,#0a0818,#120e26)',
      border: '1px solid rgba(139,92,246,0.2)',
    };
  }
  if (bgStyle === 'transparent') {
    return { background: 'transparent', border: 'none' };
  }
  return {
    background: 'linear-gradient(135deg,rgba(10,8,24,0.88),rgba(18,14,38,0.88))',
    border: '1px solid rgba(139,92,246,0.16)',
  };
}

export function LatestEventsWidget({
  fields,
  latestEvent,
  streamerTier = 'free',
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const displayMode = (fields.displayMode as 'bar' | 'rotate' | undefined) ?? 'bar';
  const rotationSpeed = Number(fields.rotationSpeed ?? 4) * 1000;
  const accent = String(fields.accentColor ?? '#8b5cf6');
  const text = String(fields.textColor ?? '#ffffff');
  const bgStyle = String(fields.bgStyle ?? 'semi');
  const fontSize = Number(fields.fontSize ?? 14);
  const showPlatformIcon = (fields.showPlatformIcon as boolean | undefined) ?? true;

  const enabled = useMemo<Slot[]>(() => {
    const raw = fields.enabledEvents as string[] | undefined;
    if (!raw?.length) return DEFAULT_ENABLED;
    return raw.filter((s): s is Slot => s in SLOT_LABEL);
  }, [fields.enabledEvents]);

  const [slots, setSlots] = useState<Partial<Record<Slot, SlotState>>>({});
  const [rotateIdx, setRotateIdx] = useState(0);
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    lastId.current = latestEvent.id;
    const ss = eventToSlot(latestEvent);
    if (!ss) return;
    if (!enabled.includes(ss.slot)) return;
    setSlots((prev) => ({ ...prev, [ss.slot]: ss }));
  }, [latestEvent, enabled]);

  useEffect(() => {
    if (displayMode !== 'rotate') return;
    const t = setInterval(
      () => setRotateIdx((i) => (i + 1) % Math.max(1, enabled.length)),
      rotationSpeed,
    );
    return () => clearInterval(t);
  }, [displayMode, rotationSpeed, enabled.length]);

  const bgCss = backgroundFor(bgStyle);
  const cssVars: React.CSSProperties = {
    ['--accent' as string]: accent,
    color: text,
  };

  const watermarkFooter = streamerTier === 'free' && (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '4px 12px 8px',
        borderTop: `1px solid ${accent}1f`,
      }}
    >
      <Watermark variant="inline" />
    </div>
  );

  if (displayMode === 'rotate') {
    const current = enabled[rotateIdx];
    const slot = current ? slots[current] : undefined;
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 8,
          overflow: 'hidden',
          fontFamily: "'Poppins', system-ui, sans-serif",
          fontSize,
          ...bgCss,
          ...cssVars,
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {slot ? (
            <Slot row={slot} accent={accent} showPlatformIcon={showPlatformIcon} />
          ) : (
            <div style={{ opacity: 0.4, fontWeight: 600 }}>
              {current ? `Waiting for ${SLOT_LABEL[current].toLowerCase()}…` : 'No events yet'}
            </div>
          )}
        </div>
        {watermarkFooter}
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily: "'Poppins', system-ui, sans-serif",
        fontSize,
        ...bgCss,
        ...cssVars,
      }}
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 2 }}>
        {enabled.map((slotKey, i) => {
          const slot = slots[slotKey];
          return (
            <div
              key={slotKey}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                opacity: slot ? 1 : 0.3,
                borderLeft: i === 0 ? 'none' : `1px solid ${accent}40`,
              }}
            >
              {slot ? (
                <Slot row={slot} accent={accent} showPlatformIcon={showPlatformIcon} />
              ) : (
                <EmptySlot key={slotKey} slot={slotKey} accent={accent} />
              )}
            </div>
          );
        })}
      </div>
      {watermarkFooter}
    </div>
  );
}

function SlotIcon({ slot, accent }: { slot: Slot; accent: string }) {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: `${accent}26`,
      }}
    >
      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={SLOT_ICON[slot]} />
      </svg>
    </div>
  );
}

function Slot({
  row,
  accent,
  showPlatformIcon,
}: {
  row: SlotState;
  accent: string;
  showPlatformIcon: boolean;
}) {
  return (
    <>
      <SlotIcon slot={row.slot} accent={accent} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            opacity: 0.45,
            lineHeight: 1,
            marginBottom: 2,
          }}
        >
          {row.label}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            }}
          >
            {row.name}
          </span>
          {row.amount && (
            <span style={{ fontWeight: 500, opacity: 0.6, whiteSpace: 'nowrap' }}>
              {row.amount}
            </span>
          )}
        </div>
      </div>
      {showPlatformIcon && (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: PLATFORM_BADGE[row.platform] ?? accent,
            opacity: 0.7,
            flexShrink: 0,
          }}
        />
      )}
    </>
  );
}

function EmptySlot({ slot, accent }: { slot: Slot; accent: string }) {
  return (
    <>
      <SlotIcon slot={slot} accent={accent} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            opacity: 0.35,
            lineHeight: 1,
          }}
        >
          {SLOT_LABEL[slot]}
        </div>
        <div style={{ fontWeight: 500, opacity: 0.35, fontSize: 12, marginTop: 2 }}>—</div>
      </div>
    </>
  );
}
